import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux";
import { createUserCategories } from "../../../../redux";
import { getTypeCategories } from "../../../../redux/Actions/ClientActions";
import { Check, ChevronDown, ChevronRight, Tag, X } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import Button from "../../../../components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { useTranslation } from "react-i18next";
import { getTranslatedCategoryName, getTranslatedSubcategories } from "../../../../utils/categoryTranslation";

interface Category {
    id: string;
    name: string;
    description: string;
    subcategories: string[]; // Backend sends subcategories as string array
    translatedName?: Record<string, string>; // Translations: { it: "Nome italiano", es: "Nombre español", ... }
    translatedSubCategories?: Record<string, string[]>; // Translations: { it: ["Sottocategoria1", ...], es: [...], ... }
    isEmergency?: boolean;
    isRequireVerification?: boolean;
    type?: { id: string | number };
}

interface Subcategory {
    id: string; // Use subcategory name as ID since backend doesn't provide separate IDs
    name: string;
    description?: string;
}

interface CategorySelectionProps {
    selectedCategories: Category[];
    selectedSubcategories: Subcategory[];
    onCategoriesChange: (categories: Category[]) => void;
    onSubcategoriesChange: (subcategories: Subcategory[]) => void;
    disabled?: boolean;
    professionalType?: number; // Add professional type prop
    resetKey?: number; // Add reset key prop to trigger search input reset
}



const CategorySelection: React.FC<CategorySelectionProps> = ({
    selectedCategories,
    selectedSubcategories,
    onCategoriesChange,
    onSubcategoriesChange,
    disabled = false,
    professionalType: propProfessionalType,
    resetKey
}) => {
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.split("-")[0];
    const { categoriesByType } = useSelector((state: any) => state.client);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [hasInitialized, setHasInitialized] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [newTags, setNewTags] = useState("");

    // Memoized cached categories for current type
    const cachedCategories = useMemo(() => {
        return propProfessionalType ? categoriesByType[propProfessionalType] || [] : [];
    }, [propProfessionalType, categoriesByType]);

    // Filtered categories based on search query
    const filteredCategories = useMemo(() => {
        if (!searchQuery || searchQuery.length < 3) {
            return []; // Return empty array initially - no categories shown
        }

        const query = searchQuery.toLowerCase();
        return categories.filter(category => {
            // Check if category name matches (including translated name)
            const translatedName = getTranslatedCategoryName(category, currentLang);
            const categoryMatches = category.name.toLowerCase().includes(query) || 
                                   translatedName.toLowerCase().includes(query);

            // Check if any subcategory matches (including translated subcategories)
            const translatedSubcats = getTranslatedSubcategories(category, currentLang) || [];
            const subcategoryMatches = (category.subcategories && category.subcategories.some(sub =>
                sub.toLowerCase().includes(query)
            )) || (Array.isArray(translatedSubcats) && translatedSubcats.some(sub => sub.toLowerCase().includes(query)));

            return categoryMatches || subcategoryMatches;
        });
    }, [categories, searchQuery, currentLang]);

    // Auto-expand categories that contain matching subcategories when searching
    useEffect(() => {
        if (searchQuery && searchQuery.length >= 3) {
            const query = searchQuery.toLowerCase();
            const newExpanded = new Set(expandedCategories);

            categories.forEach(category => {
                const translatedSubcats = getTranslatedSubcategories(category, currentLang) || [];
                const hasMatchingSubcategory = (category.subcategories && category.subcategories.some(sub =>
                    sub.toLowerCase().includes(query)
                )) || (Array.isArray(translatedSubcats) && translatedSubcats.some(sub =>
                    sub.toLowerCase().includes(query)
                ));

                if (hasMatchingSubcategory) {
                    newExpanded.add(category.id);
                }
            });

            setExpandedCategories(newExpanded);
        }
    }, [searchQuery, categories, currentLang]);

    // Reset initialization when professional type changes
    useEffect(() => {
        setHasInitialized(false);
    }, [propProfessionalType]);

    // Fetch categories when category type changes
    useEffect(() => {
        if (propProfessionalType && !hasInitialized) {
            if (cachedCategories && cachedCategories.length > 0) {
                // Use cached data - no loading state needed
                setCategories(cachedCategories);
                setLoading(false);
                setHasInitialized(true);
            } else {
                // Fetch from backend if not cached
                fetchCategories(propProfessionalType);
                setHasInitialized(true);
            }
        }
    }, [propProfessionalType, cachedCategories, hasInitialized]);

    // Clear search query when resetKey changes
    useEffect(() => {
        setSearchQuery("");
    }, [resetKey]);

    // Helper function to get cached categories for a specific type
    const getCachedCategories = (typeId: number): Category[] => {
        return categoriesByType[typeId] || [];
    };

    const fetchCategories = async (typeId: number) => {
        setLoading(true);
        try {
            const response = await dispatch(getTypeCategories({ id: typeId })).unwrap();

            // Check if response has data property or is the data directly
            let categoriesData = null;
            if (response && response.data) {
                categoriesData = response.data;
            } else if (response && Array.isArray(response)) {
                categoriesData = response;
            } else if (response && response.length > 0) {
                categoriesData = response;
            }

            if (categoriesData && categoriesData.length > 0) {
                setCategories(categoriesData);
                // Note: The Redux slice will automatically cache this data
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = async () => {
        if (!newCategory.trim()) return;
        const tags = newTags
            .split(",")
            .map(tag => tag.trim())
            .filter(tag => tag !== "");

        const payload = {
            name: newCategory.trim(),
            description: "",
            requirements: [],
            subcategories: tags,
            isRequireVerification: true,
            isEmergency: false,
            type: { id: Number(propProfessionalType) },
        };

        const res: any = await dispatch(createUserCategories({ data: payload }));
        if (createUserCategories.fulfilled.match(res) && propProfessionalType) {
            await fetchCategories(propProfessionalType);
            setNewCategory("");
            setNewTags("");
            setIsDialogOpen(false);
        }
    };



    const toggleCategoryExpansion = (categoryId: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId);
        } else {
            newExpanded.add(categoryId);
        }
        setExpandedCategories(newExpanded);
    };

    const handleCategoryToggle = (category: Category) => {
        const isSelected = isCategorySelected(category.id);

        console.log("=== Category Toggle Debug ===");
        console.log("Category:", category);
        console.log("Category ID:", category.id, "Type:", typeof category.id);
        console.log("Is Selected:", isSelected);
        console.log("Selected Categories:", selectedCategories.map(cat => ({ id: cat.id, name: cat.name, type: typeof cat.id })));
        console.log("============================");

        if (isSelected) {
            // Remove category and all its subcategories
            const newCategories = selectedCategories.filter(cat => cat.id !== category.id);
            const newSubcategories = selectedSubcategories.filter(sub =>
                !category.subcategories || !category.subcategories.includes(sub.name)
            );
            console.log("Removing - New Categories:", newCategories);
            console.log("Calling onCategoriesChange with:", newCategories);
            onCategoriesChange(newCategories);
            onSubcategoriesChange(newSubcategories);
        } else {
            // Add category
            const newCategories = [...selectedCategories, category];
            console.log("Adding - Category:", category);
            console.log("Calling onCategoriesChange with:", newCategories);
            onCategoriesChange(newCategories);
        }
    };

    const handleSubcategoryToggle = (subcategoryName: string, categoryId: string) => {
        const isSelected = selectedSubcategories.some(sub => sub.name === subcategoryName);
        if (isSelected) {
            // Remove subcategory
            const newSubcategories = selectedSubcategories.filter(sub => sub.name !== subcategoryName);
            onSubcategoriesChange(newSubcategories);
        } else {
            // Add subcategory
            const newSubcategory: Subcategory = {
                id: subcategoryName, // Use subcategory name as ID since backend doesn't provide separate IDs
                name: subcategoryName,
                description: ""
            };
            onSubcategoriesChange([...selectedSubcategories, newSubcategory]);
            const category = categories.find(cat => cat.id === categoryId);
            const isSelected = isCategorySelected(categoryId);
            if (category && !isSelected) {
                handleCategoryToggle(category);
            }
        }
    };

    const isCategorySelected = (categoryId: string) => {
        const result = selectedCategories.some(cat => cat.id === categoryId);
        console.log("isCategorySelected check:", {
            categoryId,
            categoryIdType: typeof categoryId,
            selectedCategories: selectedCategories.map(cat => ({ id: cat.id, type: typeof cat.id })),
            result
        });
        return result;
    };

    const isSubcategorySelected = (subcategoryName: string) => {
        return selectedSubcategories.some(sub => sub.name === subcategoryName);
    };

    // Helper function to highlight matching text
    const highlightText = (text: string, query: string) => {
        if (!query || query.length < 3) return text;

        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} className="bg-yellow-200 px-1 rounded">
                    {part}
                </mark>
            ) : part
        );
    };

    const getSelectedSubcategoriesCount = (categoryId: string) => {
        return selectedSubcategories.filter(sub => {
            const category = categories.find(cat => cat.id === categoryId);
            return category?.subcategories && category.subcategories.includes(sub.name);
        }).length;
    };



    return (
        <>
        <div className="space-y-6">
            {/* Categories Section */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                        {t("categorySelection.serviceCategories")}
                    </label>
                    {categories.length > 0 && !loading && (
                        <span className="text-xs text-gray-500">
                            {cachedCategories.length > 0 ? `📦 ${t("categorySelection.cached")}` : `🌐 ${t("categorySelection.fresh")}`}
                        </span>
                    )}
                </div>

                {/* Search Bar */}
                {!loading && (
                    <div className="mb-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t("categorySelection.searchPlaceholder")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                disabled={disabled}
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSearchQuery("");
                                    }}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                    disabled={disabled}
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        {searchQuery.length > 0 && searchQuery.length < 3 && (
                            <p className="text-sm text-amber-600 mt-1">
                                {t("categorySelection.minCharactersWarning")}
                            </p>
                        )}
                        {searchQuery.length >= 3 && filteredCategories.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                                {t("categorySelection.foundResults", { count: filteredCategories.length, query: searchQuery, context: filteredCategories.length === 1 ? "one" : "other" })}
                            </p>
                        )}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-gray-600">{t("categorySelection.loadingCategories")}</span>
                    </div>
                ) : categories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>{t("categorySelection.noCategoriesAvailable")}</p>
                        <p className="text-sm text-gray-400 mt-1">{t("categorySelection.categoriesAddedByAdmin")}</p>
                    </div>
                ) : searchQuery.length >= 3 && filteredCategories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 0114 0z" />
                        </svg>
                        <p>{t("categorySelection.noResultsFound", { query: searchQuery })}</p>
                        <p className="text-sm text-gray-400 mt-1">{t("categorySelection.tryDifferentTerm")}</p>
                        {!disabled && (
                            <div className="mt-4">
                                <Button size="sm" onClick={() => setIsDialogOpen(true)}>{t("categorySelection.addCategoryButton")}</Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            {filteredCategories.map((category) => (
                                <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                    {/* Category Header */}
                                    <div className="bg-gray-50 p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleCategoryExpansion(category.id);
                                                    }}
                                                    disabled={disabled}
                                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                                >
                                                    {expandedCategories.has(category.id) ? (
                                                        <ChevronDown className="w-4 h-4" />
                                                    ) : (
                                                        <ChevronRight className="w-4 h-4" />
                                                    )}
                                                </button>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={isCategorySelected(category.id)}
                                                        onChange={() => {
                                                            handleCategoryToggle(category);
                                                        }}
                                                    disabled={disabled}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="font-medium">
                                                    {highlightText(getTranslatedCategoryName(category, currentLang), searchQuery)}
                                                </span>
                                                {category.isEmergency && (
                                                        <Badge variant="destructive" className="text-xs">
                                                            {t("categorySelection.emergency")}
                                                        </Badge>
                                                    )}
                                                    {/* {category.isRequireVerification && (
                                                        <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                                                            {t("categorySelection.verificationRequired")}
                                                        </Badge>
                                                    )} */}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {getSelectedSubcategoriesCount(category.id) > 0 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        {getSelectedSubcategoriesCount(category.id)} {t("categorySelection.selected")}
                                                    </Badge>
                                                )}
                                                <span className="text-sm text-gray-500">
                                                    {category.subcategories?.length || 0} {t("categorySelection.subcategories")}
                                                    {searchQuery.length >= 3 && (() => {
                                                        const translatedSubcats = getTranslatedSubcategories(category, currentLang) || [];
                                                        const hasMatch = (category.subcategories && category.subcategories.some(sub =>
                                                            sub.toLowerCase().includes(searchQuery.toLowerCase())
                                                        )) || (Array.isArray(translatedSubcats) && translatedSubcats.some(sub =>
                                                            sub.toLowerCase().includes(searchQuery.toLowerCase())
                                                        ));
                                                        return hasMatch && (
                                                            <span className="ml-1 text-blue-600">{t("categorySelection.containsMatches")}</span>
                                                        );
                                                    })()}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-2 ml-8">{category.description}</p>
                                    </div>

                                    {/* Subcategories */}
                                    {expandedCategories.has(category.id) && category.subcategories && (() => {
                                        const originalSubcats = category.subcategories || [];
                                        
                                        // Get translations - handle both object and array formats
                                        const translationsRaw = category.translatedSubCategories?.[currentLang];
                                        let translatedSubcatsMap: Record<string, string> = {};
                                        
                                        if (translationsRaw) {
                                            if (Array.isArray(translationsRaw)) {
                                                // Array format: ['Installazione', 'Riparazione', ...]
                                                originalSubcats.forEach((original, index) => {
                                                    if (translationsRaw[index]) {
                                                        translatedSubcatsMap[original] = translationsRaw[index];
                                                    }
                                                });
                                            } else if (typeof translationsRaw === "object") {
                                                // Object format: {Installation: 'Installazione', Repair: 'Riparazione', ...}
                                                translatedSubcatsMap = translationsRaw;
                                            }
                                        }
                                        
                                        return (
                                        <div className="border-t border-gray-200 bg-white">
                                            <div className="p-4">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {originalSubcats.map((subcategoryName, index) => {
                                                        // Get translated name from the map, fallback to original
                                                        const displayName = translatedSubcatsMap[subcategoryName] || subcategoryName;
                                                        return (
                                                        <label
                                                            key={`${category.id}-${index}`}
                                                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSubcategorySelected(subcategoryName)
                                                                ? "border-blue-500 bg-blue-50"
                                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                                                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSubcategorySelected(subcategoryName)}
                                                                onChange={() => {
                                                                    handleSubcategoryToggle(subcategoryName, category.id);
                                                                }}
                                                                disabled={disabled}
                                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="font-medium text-sm">
                                                                    {highlightText(displayName, searchQuery)}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    );})}
                                                </div>
                                            </div>
                                        </div>
                                    );})()}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            {/* Selected Categories Summary */}
            {(selectedCategories.length > 0 || selectedSubcategories.length > 0) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-800 mb-3">{t("categorySelection.selectedCategoriesTitle")}</h3>
                    <div className="space-y-2">
                        {selectedCategories.length > 0 && (
                            <div>
                                <span className="text-xs font-medium text-blue-700">{t("categorySelection.categoriesLabel")}</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {selectedCategories.map((cat) => {
                                        // Try to find the full category from the categories list (which has translations)
                                        const fullCategory = categories.find(c => c.id === cat.id) || cat;
                                        const translatedName = getTranslatedCategoryName(fullCategory, currentLang);
                                        return (
                                        <Badge key={cat.id} variant="outline" className="text-xs bg-blue-100 border-blue-300 text-blue-800 flex items-center gap-1">
                                            {translatedName}
                                            {!disabled && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleCategoryToggle(cat);
                                                    }}
                                                    className="ml-1 hover:text-blue-600"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </Badge>
                                    );})}
                                </div>
                            </div>
                        )}
                        {selectedSubcategories.length > 0 && (
                            <div>
                                <span className="text-xs font-medium text-blue-700">{t("categorySelection.subcategoriesLabel")}</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {selectedSubcategories.map((sub) => {
                                        // Find the parent category to get translated subcategory name
                                        const parentCategory = categories.find(cat => 
                                            cat.subcategories && cat.subcategories.includes(sub.name)
                                        );
                                        let displayName = sub.name;
                                        if (parentCategory) {
                                            // Get translations - handle both object and array formats
                                            const translationsRaw = parentCategory.translatedSubCategories?.[currentLang];
                                            if (translationsRaw) {
                                                if (Array.isArray(translationsRaw)) {
                                                    // Array format: use index
                                                    const originalSubcats = parentCategory.subcategories || [];
                                                    const subIndex = originalSubcats.indexOf(sub.name);
                                                    if (subIndex !== -1 && translationsRaw[subIndex]) {
                                                        displayName = translationsRaw[subIndex];
                                                    }
                                                } else if (typeof translationsRaw === "object") {
                                                    // Object format: direct lookup
                                                    displayName = translationsRaw[sub.name] || sub.name;
                                                }
                                            }
                                        }
                                        return (
                                        <Badge key={sub.id} variant="outline" className="text-xs bg-green-100 border-green-300 text-green-800 flex items-center gap-1">
                                            {displayName}
                                            {!disabled && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleSubcategoryToggle(sub.name, "");
                                                    }}
                                                    className="ml-1 hover:text-green-600"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </Badge>
                                    );})}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
        {/* Add Category Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent>
                <DialogHeader className="flex justify-between items-center">
                    <DialogTitle>{t("categorySelection.addNewCategory")}</DialogTitle>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 rounded-full"
                        onClick={() => setIsDialogOpen(false)}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium">{t("categorySelection.categoryName")}</label>
                        <Input
                            value={newCategory}
                            onChange={e => setNewCategory(e.target.value)}
                            placeholder={t("categorySelection.categoryNamePlaceholder")}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">{t("categorySelection.subcategoriesCommaSeparated")}</label>
                        <Input
                            value={newTags}
                            onChange={e => setNewTags(e.target.value)}
                            placeholder={t("categorySelection.subcategoriesPlaceholder")}
                            className="mt-1"
                        />
                    </div>
                    <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                            {t("categorySelection.cancel")}
                        </Button>
                        <Button onClick={handleAddCategory}>{t("categorySelection.addCategory")}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    </>
    );
};

export default CategorySelection;
